import { useState, useEffect, useCallback, useRef } from "react";
import { ConfirmModal, ActionButtons, useToast } from "../../../../shared/components/SharedUI";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAsientos } from "../hooks/useAsientos";
import apiFetch from "../../../../shared/services/apiClient";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { downloadExcel, uploadExcel } from "../../shared/excelService";
import styles from "../../invoices/styles/invoice.module.css";

const TIPO_COLOR = {
  MANUAL:  { bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.3)", text: "#a78bfa" },
  FACTURA: { bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.3)",  text: "#4ade80" },
  CXP:     { bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.3)",  text: "#fbbf24" },
  RECIBO:  { bg: "rgba(96,165,250,0.1)",  border: "rgba(96,165,250,0.3)",  text: "#60a5fa" },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2 });

// ── Modal de detalle / edición — theme-aware ──────────────────────────────────
function AsientoDetalleModal({ asientoId, onClose, onDeleted, onSaved }) {
  const { t }       = useTranslation();
  const multimoneda = useMultimoneda();
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [concepto, setConcepto] = useState("");
  const [fecha,    setFecha]    = useState("");
  const [lineas,   setLineas]   = useState([]);
  const [cuentas,  setCuentas]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm,  setConfirm]  = useState(false);
  const [toastMsg, setToastMsg] = useState({ msg: "", ok: true });

  const showT = (msg, ok = true) => { setToastMsg({ msg, ok }); setTimeout(() => setToastMsg({ msg: "", ok: true }), 3500); };

  function loadAsiento() {
    setLoading(true);
    apiFetch(`/api/finance/asientos/${asientoId}/`)
      .then(r => r.json())
      .then(d => {
        setData(d); setConcepto(d.concepto); setFecha(d.fecha || "");
        setLineas(d.lineas?.map(l => ({
          cuenta: l.cuenta, cuenta_codigo: l.cuenta_codigo, cuenta_nombre: l.cuenta_nombre,
          debito: Number(l.debito) || 0, credito: Number(l.credito) || 0, descripcion: l.descripcion || "",
          debito_usd: Number(l.debito_usd) || 0, credito_usd: Number(l.credito_usd) || 0,
          moneda: l.moneda || "CRC", tipo_cambio: Number(l.tipo_cambio) || 1,
        })) || []);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadAsiento();
    apiFetch("/api/finance/catalogo/?is_detalle=true&page_size=1000&ordering=codigo")
      .then(r => r.json()).then(d => setCuentas(d.results || d)).catch(() => {});
  }, [asientoId]);

  const totalD    = lineas.reduce((s, l) => s + (l.debito       || 0), 0);
  const totalH    = lineas.reduce((s, l) => s + (l.credito      || 0), 0);
  const totalDusd = lineas.reduce((s, l) => s + (Number(l.debito_usd)  || 0), 0);
  const totalHusd = lineas.reduce((s, l) => s + (Number(l.credito_usd) || 0), 0);

  const balanced    = Math.abs(totalD - totalH) < 0.001;
  const hasUsd      = multimoneda && (totalDusd > 0 || totalHusd > 0);
  const balancedUsd = !hasUsd || Math.abs(totalDusd - totalHusd) < 0.001;
  const fullyOk     = balanced && balancedUsd;

  const updL = (idx, field, val) => setLineas(ls => ls.map((l, i) => i === idx ? { ...l, [field]: val } : l));
  const addL = () => setLineas(ls => [...ls, {
    cuenta: null, cuenta_codigo: "", cuenta_nombre: "",
    debito: 0, credito: 0, descripcion: "",
    debito_usd: 0, credito_usd: 0,
  }]);
  const delL = (idx) => { if (lineas.length <= 2) { showT("Mínimo 2 líneas", false); return; } setLineas(ls => ls.filter((_, i) => i !== idx)); };
  const chgC = (idx, id) => {
    const c = cuentas.find(x => String(x.id) === String(id));
    setLineas(ls => ls.map((l, i) => i === idx ? { ...l, cuenta: c?.id || null, cuenta_codigo: c?.codigo || "", cuenta_nombre: c?.nombre || "" } : l));
  };

  async function handleSave() {
    if (!balanced)    { showT(t("asientos.noBalanceado"), false); return; }
    if (!balancedUsd) { showT("Asiento USD no balanceado. Revisá los montos en dólares.", false); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/finance/asientos/${asientoId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          concepto,
          fecha,
          lineas: lineas.map(l => ({
            cuenta:      l.cuenta,
            debito:      l.debito      || 0,
            credito:     l.credito     || 0,
            descripcion: l.descripcion || "",
            debito_usd:  multimoneda ? (Number(l.debito_usd)  || 0) : 0,
            credito_usd: multimoneda ? (Number(l.credito_usd) || 0) : 0,
            moneda:      multimoneda && (Number(l.debito_usd) > 0 || Number(l.credito_usd) > 0) ? "USD" : "CRC",
          })),
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || JSON.stringify(e)); }
      showT("Guardado correctamente ✓");
      setEditMode(false); loadAsiento(); onSaved?.();
    } catch (e) { showT(e.message || "Error al guardar", false); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try { await apiFetch(`/api/finance/asientos/${asientoId}/`, { method: "DELETE" }); onDeleted?.(); onClose(); }
    catch (_) {} finally { setDeleting(false); }
  }

  const inpSt = {
    background: "var(--bg-input)", border: "1px solid var(--border-input)",
    borderRadius: 5, color: "var(--text-primary)", fontSize: 13,
    padding: "5px 8px", outline: "none", width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)",
      backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, width: "min(840px,96vw)", maxHeight: "92vh", display: "flex",
        flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--text-secondary)" }}>
            <i className="fa-solid fa-circle-notch fa-spin"
               style={{ color: "#f97316", fontSize: "1.4rem", display: "block", marginBottom: 8 }}/>
            {t("common.cargando")}
          </div>
        ) : data ? (<>

          {/* Header */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
            display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)", fontFamily: "monospace" }}>
                {data.numero}
              </h2>
              <p style={{ margin: "3px 0 0", color: "var(--text-secondary)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                {editMode ? (
                  <input
                    type="date"
                    value={fecha}
                    onChange={e => setFecha(e.target.value)}
                    style={{
                      background: "var(--bg-input)", border: "1px solid #f97316",
                      borderRadius: 5, color: "var(--text-primary)", fontSize: 13,
                      padding: "3px 7px", outline: "none",
                    }}
                  />
                ) : (
                  <span>{data.fecha}</span>
                )}
                · {data.tipo_display}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {!editMode && (
                <button type="button" onClick={() => setEditMode(true)}
                  style={{ padding: "6px 12px", background: "transparent", border: "1px solid var(--border-card)",
                    borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                    display: "flex", alignItems: "center", gap: 6 }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = "#f97316"; e.currentTarget.style.color = "#f97316"; }}
                  onMouseOut={e  => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                  <i className="fa-solid fa-pen-to-square"/> {t("common.editar")}
                </button>
              )}
              {!editMode && (!confirm ? (
                <button type="button" onClick={() => setConfirm(true)}
                  style={{ padding: "6px 12px", background: "transparent", border: "1px solid var(--border-card)",
                    borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                    display: "flex", alignItems: "center", gap: 6 }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.color = "#f87171"; }}
                  onMouseOut={e  => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                  <i className="fa-solid fa-trash"/> {t("common.eliminar")}
                </button>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ color: "#f87171", fontSize: 12 }}>¿{t("common.eliminar")}?</span>
                  <button type="button" onClick={handleDelete} disabled={deleting}
                    style={{ padding: "5px 10px", background: "rgba(239,68,68,0.15)", border: "1px solid #ef4444",
                      borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {deleting ? <i className="fa-solid fa-circle-notch fa-spin"/> : t("common.siEliminar")}
                  </button>
                  <button type="button" onClick={() => setConfirm(false)}
                    style={{ padding: "5px 10px", background: "transparent", border: "1px solid var(--border-card)",
                      borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 12 }}>
                    {t("common.cancelar")}
                  </button>
                </div>
              ))}
              <button type="button" onClick={onClose}
                style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "1.1rem", padding: "4px 6px" }}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
          </div>

          {/* Toast */}
          {toastMsg.msg && (
            <div style={{ padding: "8px 20px",
              background: toastMsg.ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
              borderBottom: "1px solid var(--border-card)",
              color: toastMsg.ok ? "#4ade80" : "#f87171", fontSize: 13,
              display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <i className={`fa-solid ${toastMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}/>
              {toastMsg.msg}
            </div>
          )}

          {/* Aviso tipo automático */}
          {editMode && data.tipo !== "MANUAL" && (
            <div style={{ padding: "10px 20px", background: "rgba(251,191,36,0.08)",
              borderBottom: "1px solid rgba(251,191,36,0.2)", fontSize: 12, color: "#fbbf24",
              display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <i className="fa-solid fa-triangle-exclamation"/>
              {t("asientos.asientoAutomatico")}
            </div>
          )}
          {/* Aviso: multimoneda desactivado — columnas USD en solo lectura */}
          {editMode && !multimoneda && (
            <div style={{ padding: "8px 20px", background: "rgba(100,116,139,0.07)",
              borderBottom: "1px solid rgba(100,116,139,0.15)", fontSize: 12, color: "var(--text-secondary)",
              display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <i className="fa-solid fa-lock" style={{ fontSize: 10 }}/>
              Los campos en dólares (USD) están desactivados. Activá Multimoneda en Configuración para editarlos.
            </div>
          )}

          {/* Concepto */}
          <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--border-card)", flexShrink: 0 }}>
            {editMode ? (
              <div>
                <label style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase",
                  letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>
                  {t("asientos.concepto")}
                </label>
                <input type="text" value={concepto} onChange={e => setConcepto(e.target.value)}
                  style={{ ...inpSt, border: "1px solid #f97316" }}/>
              </div>
            ) : (
              <p style={{ margin: 0, color: "var(--text-primary)", fontSize: 14 }}>{data.concepto}</p>
            )}
          </div>

          {/* Tabla líneas */}
          <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "thin" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "var(--bg-card)", zIndex: 1 }}>
                <tr style={{ borderBottom: "1px solid var(--border-card)" }}>
                  {[t("catalogo.nombre"), t("common.descripcion")].map(h => (
                    <th key={h} style={{ padding: "10px 12px", color: "var(--text-secondary)", fontSize: 11,
                      fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left" }}>
                      {h}
                    </th>
                  ))}
                  <th style={{ padding: "10px 12px", color: "#4ade80", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                    {t("asientos.debe")} ₡
                  </th>
                  <th style={{ padding: "10px 12px", color: "#60a5fa", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                    {t("asientos.haber")} ₡
                  </th>
                  <th style={{ padding: "10px 12px", color: "#86efac", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right", borderLeft: "1px dashed var(--border-card)" }}>
                    {t("asientos.debe")} $
                  </th>
                  <th style={{ padding: "10px 12px", color: "#93c5fd", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                    {t("asientos.haber")} $
                  </th>
                  {editMode && <th style={{ width: 32 }}/>}
                </tr>
              </thead>
              <tbody>
                {lineas.map((l, i) => (
                  <tr key={i} style={{ background: "transparent",
                    borderBottom: "1px solid var(--border-card)" }}>
                    <td style={{ padding: "6px 8px" }}>
                      {editMode ? (
                        <select value={l.cuenta || ""} onChange={e => chgC(i, e.target.value)} style={inpSt}>
                          <option value="">{cuentas.length === 0 ? t("common.cargando") : `— ${t("common.seleccionar")} —`}</option>
                          {l.cuenta && !cuentas.find(c => String(c.id) === String(l.cuenta)) && (
                            <option value={l.cuenta}>{l.cuenta_codigo} — {l.cuenta_nombre}</option>
                          )}
                          {cuentas.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                        </select>
                      ) : (
                        <>
                          <span style={{ fontFamily: "monospace", color: "#f97316", fontSize: 11 }}>{l.cuenta_codigo}</span>
                          <span style={{ color: "var(--text-secondary)", marginLeft: 8 }}>{l.cuenta_nombre}</span>
                        </>
                      )}
                    </td>
                    <td style={{ padding: "6px 8px", maxWidth: 220, overflow: "hidden" }}>
                      {editMode ? (
                        <input type="text" value={l.descripcion} style={inpSt}
                          onChange={e => updL(i, "descripcion", e.target.value)}
                          placeholder={t("common.descripcion")}/>
                      ) : (
                        <span style={{ padding: "9px 12px", color: "var(--text-secondary)",
                          display: "block", whiteSpace: "nowrap", overflow: "hidden",
                          textOverflow: "ellipsis" }}
                          title={l.descripcion || ""}>
                          {l.descripcion || "—"}
                        </span>
                      )}
                    </td>
                    {/* DEBE ₡ */}
                    <td style={{ padding: "6px 8px" }}>
                      {editMode ? (
                        <input type="number" min="0" step="0.01" value={l.debito || ""} style={{ ...inpSt, textAlign: "right", color: "#4ade80" }}
                          onChange={e => { updL(i, "debito", Number(e.target.value) || 0); if (Number(e.target.value) > 0) updL(i, "credito", 0); }}/>
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: l.debito > 0 ? "#4ade80" : "var(--border-card)" }}>
                          {l.debito > 0 ? `₡${fmt(l.debito)}` : "—"}
                        </div>
                      )}
                    </td>
                    {/* HABER ₡ */}
                    <td style={{ padding: "6px 8px" }}>
                      {editMode ? (
                        <input type="number" min="0" step="0.01" value={l.credito || ""} style={{ ...inpSt, textAlign: "right", color: "#60a5fa" }}
                          onChange={e => { updL(i, "credito", Number(e.target.value) || 0); if (Number(e.target.value) > 0) updL(i, "debito", 0); }}/>
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: l.credito > 0 ? "#60a5fa" : "var(--border-card)" }}>
                          {l.credito > 0 ? `₡${fmt(l.credito)}` : "—"}
                        </div>
                      )}
                    </td>
                    {/* DEBE $ */}
                    <td style={{ padding: "6px 8px", borderLeft: "1px dashed var(--border-card)" }}>
                      {editMode && multimoneda ? (
                        <input type="number" min="0" step="0.01"
                          value={Number(l.debito_usd) || ""}
                          style={{ ...inpSt, textAlign: "right", color: "#86efac" }}
                          placeholder="0.00"
                          onChange={e => {
                            updL(i, "debito_usd", Number(e.target.value) || 0);
                            if (Number(e.target.value) > 0) updL(i, "credito_usd", 0);
                          }}
                        />
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: Number(l.debito_usd) > 0 ? "#86efac" : "var(--border-card)" }}>
                          {Number(l.debito_usd) > 0 ? `$${fmt(l.debito_usd)}` : "—"}
                        </div>
                      )}
                    </td>
                    {/* HABER $ */}
                    <td style={{ padding: "6px 8px" }}>
                      {editMode && multimoneda ? (
                        <input type="number" min="0" step="0.01"
                          value={Number(l.credito_usd) || ""}
                          style={{ ...inpSt, textAlign: "right", color: "#93c5fd" }}
                          placeholder="0.00"
                          onChange={e => {
                            updL(i, "credito_usd", Number(e.target.value) || 0);
                            if (Number(e.target.value) > 0) updL(i, "debito_usd", 0);
                          }}
                        />
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: Number(l.credito_usd) > 0 ? "#93c5fd" : "var(--border-card)" }}>
                          {Number(l.credito_usd) > 0 ? `$${fmt(l.credito_usd)}` : "—"}
                        </div>
                      )}
                    </td>
                    {editMode && (
                      <td style={{ padding: "4px", textAlign: "center" }}>
                        <button type="button" onClick={() => delL(i)}
                          style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>
                          <i className="fa-solid fa-times"/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {editMode && (
                  <tr><td colSpan={7} style={{ padding: "6px 8px" }}>
                    <button type="button" onClick={addL}
                      style={{ background: "transparent", border: "1px dashed var(--border-card)",
                        borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer",
                        padding: "5px 12px", fontSize: 12, width: "100%",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      <i className="fa-solid fa-plus"/> {t("asientos.agregarLinea")}
                    </button>
                  </td></tr>
                )}
                <tr style={{ borderTop: "2px solid var(--border-card)", background: "rgba(255,255,255,0.04)" }}>
                  <td colSpan={2} style={{ padding: "10px 12px", color: "var(--text-muted)",
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {t("asientos.totales")}
                  </td>
                  <td><div style={{ padding: "10px 12px", textAlign: "right", color: "#4ade80", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                    ₡{fmt(editMode ? totalD : data.total_debitos)}
                  </div></td>
                  <td><div style={{ padding: "10px 12px", textAlign: "right", color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                    ₡{fmt(editMode ? totalH : data.total_creditos)}
                  </div></td>
                  {/* Totales USD */}
                  <td style={{ borderLeft: "1px dashed var(--border-card)" }}>
                    <div style={{ padding: "10px 12px", textAlign: "right", color: "#86efac", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                      {editMode
                        ? (totalDusd > 0 ? `$${fmt(totalDusd)}` : "—")
                        : (() => { const t$ = (data?.lineas || []).reduce((s, l) => s + (Number(l.debito_usd) || 0), 0); return t$ > 0 ? `$${fmt(t$)}` : "—"; })()}
                    </div>
                  </td>
                  <td>
                    <div style={{ padding: "10px 12px", textAlign: "right", color: "#93c5fd", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                      {editMode
                        ? (totalHusd > 0 ? `$${fmt(totalHusd)}` : "—")
                        : (() => { const t$ = (data?.lineas || []).reduce((s, l) => s + (Number(l.credito_usd) || 0), 0); return t$ > 0 ? `$${fmt(t$)}` : "—"; })()}
                    </div>
                  </td>
                  {editMode && <td/>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0, fontSize: 13,
            background: (editMode ? fullyOk : data.balanceado) ? "rgba(74,222,128,0.05)" : "rgba(239,68,68,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {/* Balance CRC */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <i className={`fa-solid ${(editMode ? balanced : data.balanceado) ? "fa-circle-check" : "fa-triangle-exclamation"}`}
                   style={{ color: (editMode ? balanced : data.balanceado) ? "#4ade80" : "#f87171" }}/>
                <span style={{ color: (editMode ? balanced : data.balanceado) ? "#4ade80" : "#f87171" }}>
                  {(editMode ? balanced : data.balanceado) ? `₡ ${t("asientos.balanceado")}` : `₡ ${t("asientos.noBalanceado")}`}
                </span>
                {editMode && !balanced && (
                  <span style={{ color: "#f87171", fontSize: 12 }}>
                    (Dif: ₡{fmt(Math.abs(totalD - totalH))})
                  </span>
                )}
              </div>
              {/* Balance USD — solo en edit mode cuando multimoneda y hay montos USD */}
              {editMode && multimoneda && hasUsd && (
                <div style={{ display: "flex", alignItems: "center", gap: 6,
                  paddingLeft: 10, borderLeft: "1px dashed var(--border-card)" }}>
                  <i className={`fa-solid ${balancedUsd ? "fa-circle-check" : "fa-triangle-exclamation"}`}
                     style={{ color: balancedUsd ? "#86efac" : "#f87171" }}/>
                  <span style={{ color: balancedUsd ? "#86efac" : "#f87171" }}>
                    {balancedUsd ? `$ ${t("asientos.balanceado")}` : `$ ${t("asientos.noBalanceado")}`}
                  </span>
                  {!balancedUsd && (
                    <span style={{ color: "#f87171", fontSize: 12 }}>
                      (Dif: ${fmt(Math.abs(totalDusd - totalHusd))})
                    </span>
                  )}
                </div>
              )}
            </div>
            {editMode && (
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => { setEditMode(false); loadAsiento(); }}
                  style={{ padding: "7px 14px", background: "transparent", border: "1px solid var(--border-card)",
                    borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 13 }}>
                  {t("common.cancelar")}
                </button>
                <button type="button" onClick={handleSave} disabled={saving || !fullyOk}
                  style={{ padding: "7px 16px",
                    background: fullyOk ? "#f97316" : "var(--border-card)",
                    border: "none", borderRadius: 6,
                    color: fullyOk ? "#fff" : "var(--text-muted)",
                    cursor: fullyOk ? "pointer" : "not-allowed",
                    fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  {saving
                    ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                    : <><i className="fa-solid fa-floppy-disk"/> {t("cxp.guardarCambios")}</>}
                </button>
              </div>
            )}
          </div>
        </>) : (
          <div style={{ padding: 40, textAlign: "center", color: "#f87171" }}>
            {t("asientos.errorCargar")}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListAsientos() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const [page,        setPage]      = useState(1);
  const [search,      setSearch]    = useState("");
  const [tipo,        setTipo]      = useState("");
  const [detailId,    setDetailId]  = useState(null);
  const [actionMsg,   setActionMsg] = useState(null);
  const [importing,   setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { creados, errores, omitidos, detalle }
  const fileInputRef = useRef(null);

  const params = { page, ...(search && { search }), ...(tipo && { tipo }) };
  const { asientos, count, loading, error, refetch } = useAsientos(params);
  const totalPages = Math.max(1, Math.ceil(count / 20));

  // TIPO_LABEL built dynamically so it re-renders on language change
  const TIPO_LABEL = {
    MANUAL:  t("asientos.manual"),
    FACTURA: t("sidebar.facturas"),
    CXP:     "CxP",
    RECIBO:  t("sidebar.recibos"),
  };

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function handleDescargarPlantilla() {
    try {
      await downloadExcel("/api/finance/asientos/plantilla/", "plantilla_asientos.xlsx");
    } catch {
      showMsg(false, "Error al descargar la plantilla");
    }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const data = await uploadExcel("/api/finance/asientos/importar/", file);
      setImportResult(data);
      if ((data.creados ?? 0) > 0) refetch();
    } catch (err) {
      setImportResult({ creados: 0, errores: [err.message || "Error de conexión"], omitidos: 0 });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={styles.active}> {t("asientos.titulo")}</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "10px 0 20px",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text-primary)",
            display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ background: "rgba(249,115,22,0.2)", color: "#f97316", padding: "6px 8px", borderRadius: 8 }}>
              <i className="fa-solid fa-book-open" style={{ color: "#f97316" }}/>
            </span>
            {t("asientos.titulo")}
          </h2>
          <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
            {count} {t("asientos.asientosRegistrados")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* ── Plantilla Excel ── */}
          <button
            onClick={handleDescargarPlantilla}
            title="Descargar plantilla Excel para importar asientos"
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(74,222,128,0.35)",
              background: "rgba(74,222,128,0.08)", color: "#4ade80",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = "rgba(74,222,128,0.18)"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(74,222,128,0.08)"; }}
          >
            <i className="fa-solid fa-file-arrow-down" /> Plantilla
          </button>

          {/* ── Importar Excel ── */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            title="Importar asientos desde Excel"
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(96,165,250,0.35)",
              background: "rgba(96,165,250,0.08)", color: "#60a5fa",
              fontWeight: 600, fontSize: 13, cursor: importing ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
              opacity: importing ? 0.6 : 1,
            }}
            onMouseOver={(e) => { if (!importing) e.currentTarget.style.background = "rgba(96,165,250,0.18)"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; }}
          >
            {importing
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Importando…</>
              : <><i className="fa-solid fa-file-arrow-up" /> Importar Excel</>}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />

          {/* ── Nuevo asiento ── */}
          <Link to="/finanzas/asientos/nuevo" className="btn"
            style={{
              padding: "9px 18px", background: "#f97316", borderRadius: 6,
              color: "#fff", textDecoration: "none", fontWeight: 600,
              fontSize: 14, display: "flex", alignItems: "center", gap: 7, border: "none",
            }}>
            <i className="fa-solid fa-plus" /> {t("asientos.nuevoAsientoManual")}
          </Link>
        </div>
      </div>

      {/* Banner acción */}
      {actionMsg && (
        <div style={{
          padding: "10px 16px", marginBottom: 14, borderRadius: 6,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: 14, fontWeight: 500 }}>
            <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
               style={{ marginRight: 8 }} />
            {actionMsg.text}
          </span>
          <button onClick={() => setActionMsg(null)}
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      {/* Tabla contenedor */}
      <div className={styles["table-container"]}>

        {/* Filtros */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderBottom: "1px solid var(--border-card)",
          background: "var(--hover-bg)",
        }}>
          <i className="fa-solid fa-magnifying-glass" style={{ color: "var(--text-secondary)", fontSize: 12 }} />
          <input type="text"
            placeholder={t("asientos.buscarPlaceholder")}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, background: "none", border: "none", color: "var(--text-primary)", fontSize: 14, outline: "none" }} />
          <select value={tipo}
            onChange={(e) => { setTipo(e.target.value); setPage(1); }}
            style={{
              background: "var(--bg-input)", border: "1px solid var(--border-input)",
              borderRadius: 6, color: tipo ? "#f97316" : "var(--text-secondary)",
              padding: "5px 10px", fontSize: 13, cursor: "pointer", outline: "none",
            }}>
            <option value="">{t("asientos.todosTipos")}</option>
            <option value="MANUAL">{t("asientos.manual")}</option>
            <option value="FACTURA">{t("sidebar.facturas")}</option>
            <option value="CXP">CxP</option>
            <option value="RECIBO">{t("sidebar.recibos")}</option>
          </select>
        </div>

        {/* Tabla */}
        <div className={styles["table-responsive"]}>
          <div className={styles["table-body"]}>
            <table>
              <thead>
                <tr>
                  {[t("asientos.numAsiento"), t("common.fecha"), t("asientos.concepto"),
                    t("asientos.tipo"), t("asientos.debitos"), t("asientos.creditos"),
                    t("asientos.estado"), ""].map((h, i) => (
                    <th key={i} style={{
                      textAlign: [t("asientos.debitos"), t("asientos.creditos")].includes(h) ? "right" : undefined,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 40 }}>
                    <i className="fa-solid fa-circle-notch fa-spin"
                       style={{ color: "#f97316", fontSize: "1.2rem", display: "block", marginBottom: 8 }} />
                    {t("asientos.cargando")}
                  </td></tr>
                ) : error ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#f87171" }}>
                    {error}
                  </td></tr>
                ) : asientos.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
                    {t("asientos.sinAsientos")}
                  </td></tr>
                ) : asientos.map((a) => {
                  const color = TIPO_COLOR[a.tipo] || TIPO_COLOR.MANUAL;
                  return (
                    <tr key={a.id}>
                      <td style={{ fontFamily: "monospace", color: "#f97316", fontWeight: 600, fontSize: 13 }}>
                        {a.numero}
                      </td>
                      <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>{a.fecha}</td>
                      <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                        {a.concepto}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px",
                          borderRadius: 20, background: color.bg,
                          border: `1px solid ${color.border}`, color: color.text }}>
                          {TIPO_LABEL[a.tipo] || a.tipo_display || a.tipo}
                        </span>
                      </td>
                      <td>
                        <div style={{ textAlign: "right", fontFamily: "monospace", color: "#4ade80", fontSize: 13 }}>
                          ₡{fmt(a.total_debitos)}
                        </div>
                      </td>
                      <td>
                        <div style={{ textAlign: "right", fontFamily: "monospace", color: "#60a5fa", fontSize: 13 }}>
                          ₡{fmt(a.total_creditos)}
                        </div>
                      </td>
                      <td>
                        {a.balanceado
                          ? <i className="fa-solid fa-circle-check" style={{ color: "#4ade80" }} title={t("asientos.balanceado")} />
                          : <i className="fa-solid fa-triangle-exclamation" style={{ color: "#f87171" }} title={t("asientos.noBalanceado")} />
                        }
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button type="button" onClick={() => setDetailId(a.id)}
                            title={t("common.ver")}
                            style={{ background: "none", border: "none",
                              color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
                              padding: "4px 6px", transition: "color 0.2s" }}
                            onMouseOver={(e) => (e.currentTarget.style.color = "#f97316")}
                            onMouseOut={(e)  => (e.currentTarget.style.color = "var(--text-secondary)")}
                          >
                            <i className="fa-solid fa-eye" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 16px", borderTop: "1px solid var(--border-card)",
          background: "var(--hover-bg)", fontSize: 13, color: "var(--text-secondary)",
        }}>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", margin: 0 }}>
            <span>{count}</span> {t("common.resultados")} · {t("common.pagina")} <span>{page}</span> {t("common.de")}{" "}
            <span>{Math.max(1, totalPages)}</span>
          </p>
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              style={{
                padding: "5px 12px", fontSize: 13, cursor: page <= 1 ? "default" : "pointer",
                background: "transparent", borderRadius: 6,
                border: "1px solid var(--border-card)",
                color: page <= 1 ? "var(--text-muted)" : "var(--text-secondary)",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => { if (page > 1) e.currentTarget.style.color = "var(--text-primary)"; }}
              onMouseOut={(e)  => { e.currentTarget.style.color = page <= 1 ? "var(--text-muted)" : "var(--text-secondary)"; }}
            >← {t("common.anterior")}</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              style={{
                padding: "5px 12px", fontSize: 13,
                cursor: page >= totalPages ? "default" : "pointer",
                background: "transparent", borderRadius: 6,
                border: "1px solid var(--border-card)",
                color: page >= totalPages ? "var(--text-muted)" : "var(--text-secondary)",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => { if (page < totalPages) e.currentTarget.style.color = "var(--text-primary)"; }}
              onMouseOut={(e)  => { e.currentTarget.style.color = page >= totalPages ? "var(--text-muted)" : "var(--text-secondary)"; }}
            >{t("common.siguiente")} →</button>
          </div>
        </div>
      </div>

      {/* Modal detalle / editar / eliminar */}
      {detailId && (
        <AsientoDetalleModal
          asientoId={detailId}
          onClose={() => setDetailId(null)}
          onDeleted={(msg) => { refetch(); showToast(msg || "Asiento eliminado correctamente."); }}
          onSaved={() => { refetch(); showMsg(true, "Concepto actualizado correctamente."); }}
        />
      )}

      {/* ── Modal resultado importación ── */}
      {importResult && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1100, padding: 20,
        }}>
          <div style={{
            background: "var(--bg-card)", borderRadius: 12,
            border: "1px solid var(--border-card)",
            width: "100%", maxWidth: 560,
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
            display: "flex", flexDirection: "column", maxHeight: "85vh",
          }}>
            {/* Header */}
            <div style={{
              padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "var(--text-primary)",
                display: "flex", alignItems: "center", gap: 8 }}>
                <i className="fa-solid fa-file-arrow-up" style={{ color: "#60a5fa" }} />
                Resultado de importación
              </span>
              <button onClick={() => setImportResult(null)}
                style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 20px", overflowY: "auto" }}>
              {/* Estadísticas */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 8,
                  background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)", textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#4ade80" }}>{importResult.creados}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Asientos creados</div>
                </div>
                {(importResult.omitidos ?? 0) > 0 && (
                  <div style={{ flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 8,
                    background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#fbbf24" }}>{importResult.omitidos}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Omitidos</div>
                  </div>
                )}
                {(importResult.errores?.length ?? 0) > 0 && (
                  <div style={{ flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 8,
                    background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#f87171" }}>{importResult.errores.length}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Errores</div>
                  </div>
                )}
              </div>

              {/* Asientos creados */}
              {importResult.detalle?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#4ade80", marginBottom: 6 }}>
                    <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                    Asientos importados
                  </p>
                  <div style={{ background: "var(--hover-bg)", borderRadius: 8, overflow: "hidden",
                    border: "1px solid var(--border-card)" }}>
                    {importResult.detalle.map((d, i) => (
                      <div key={i} style={{
                        padding: "7px 12px", fontSize: 13, display: "flex", gap: 10, alignItems: "center",
                        borderBottom: i < importResult.detalle.length - 1 ? "1px solid var(--border-card)" : "none",
                      }}>
                        <span style={{ color: "#a78bfa", fontFamily: "monospace", fontWeight: 700, minWidth: 80 }}>{d.numero}</span>
                        <span style={{ color: "var(--text-muted)", minWidth: 90 }}>{d.fecha}</span>
                        <span style={{ color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.concepto}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errores */}
              {importResult.errores?.length > 0 && (
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#f87171", marginBottom: 6 }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                    Errores detectados
                  </p>
                  <div style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 8, padding: "10px 14px", maxHeight: 200, overflowY: "auto" }}>
                    {importResult.errores.map((e, i) => (
                      <p key={i} style={{ margin: "3px 0", fontSize: 12, color: "#f87171" }}>
                        <i className="fa-solid fa-circle-xmark" style={{ marginRight: 6 }} />{e}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-card)",
              display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setImportResult(null)}
                style={{ padding: "8px 20px", background: "#f97316", border: "none",
                  borderRadius: 6, color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {toastEl}
    </>
  );
}
